import { Injectable } from '@angular/core';

export type QuickDatePreset =
  | 'custom'
  | 'last30'
  | 'last90'
  | 'lastMonth'
  | 'mom'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'ytd'
  | 'lastYear'
  | 'ltm';

export interface DateRange {
  start: Date;
  end: Date;
}

@Injectable({ providedIn: 'root' })
export class DateRangeService {
  /**
   * Compute range for a given preset using local date rules.
   * @param preset The quick date preset to compute
   * @param referenceDate Optional reference date (defaults to today). When provided,
   *                      date ranges are calculated relative to this date instead of today.
   *                      This is used to anchor quick dates to the last transaction date.
   */
  compute(preset: QuickDatePreset, referenceDate?: Date): DateRange {
    const today = (): Date => {
      const t = new Date();
      return new Date(t.getFullYear(), t.getMonth(), t.getDate());
    };

    const refDate: Date = referenceDate
      ? new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate())
      : today();

    switch (preset) {
      case 'last30':
        return { start: this.shiftDays(refDate, -29), end: refDate };
      case 'last90':
        return { start: this.shiftDays(refDate, -89), end: refDate };
      case 'lastMonth': {
        // Previous calendar month from the 1st to the last day
        const year = refDate.getFullYear();
        const month = refDate.getMonth();
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevYear = month === 0 ? year - 1 : year;
        const start = new Date(prevYear, prevMonth, 1);
        const end = new Date(prevYear, prevMonth + 1, 0); // day 0 of next month = last day of prevMonth
        return { start, end };
      }
      case 'thisQuarter': {
        const qs = this.quarterStart(refDate);
        return { start: qs, end: refDate };
      }
      case 'lastQuarter': {
        const lastQEnd = this.addDays(this.quarterStart(refDate), -1);
        const lastQStart = this.quarterStart(this.addDays(lastQEnd, -1));
        return { start: lastQStart, end: lastQEnd };
      }
      case 'ytd': {
        const start = new Date(refDate.getFullYear(), 0, 1);
        return { start, end: refDate };
      }
      case 'lastYear': {
        const year = refDate.getFullYear() - 1;
        return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
      }
      case 'mom': {
        // Current calendar month: 1st of refDate's month to end of month,
        // but capped at refDate when refDate is in the current real month.
        const momStart = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
        const monthEnd = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);
        const realToday = today();
        const inCurrentMonth =
          refDate.getFullYear() === realToday.getFullYear() &&
          refDate.getMonth() === realToday.getMonth();
        const momEnd = inCurrentMonth ? refDate : monthEnd;
        return { start: momStart, end: momEnd };
      }
      case 'ltm': {
        // Last Twelve Months: 364 days back from refDate to refDate (365-day window)
        return { start: this.shiftDays(refDate, -364), end: refDate };
      }
      case 'custom':
      default:
        return { start: refDate, end: refDate };
    }
  }

  toIso(range: DateRange): { start: string; end: string } {
    return { start: this.format(range.start), end: this.format(range.end) };
  }

  /**
   * Attempt to match a given ISO date range to a known QuickDatePreset.
   *
   * Quick-date presets are relative to a reference anchor — usually the
   * tenant's last-transaction date (lagging tenants / historical
   * backfills set this anchor in the past), falling back to today when
   * the caller has no anchor. Without `anchorDate`, lagging-tenant
   * ranges that match e.g. `Last Quarter` against the tenant's anchor
   * would be misclassified as `'custom'` because the comparison runs
   * against today.
   *
   * Returns 'custom' when no preset matches (with or without anchor).
   */
  matchPreset(iso: { start?: string; end?: string }, anchorDate?: Date): QuickDatePreset {
    if (!iso.start || !iso.end) return 'custom';
    const start = this.parse(iso.start);
    const end = this.parse(iso.end);

    const candidates: QuickDatePreset[] = [
      'last30',
      'last90',
      'lastMonth',
      'mom',
      'thisQuarter',
      'lastQuarter',
      'ytd',
      'lastYear',
      'ltm',
    ];
    for (const p of candidates) {
      const r = this.compute(p, anchorDate);
      if (this.eq(r.start, start) && this.eq(r.end, end)) {
        return p;
      }
    }
    return 'custom';
  }

  private quarterStart(d: Date): Date {
    const month = d.getMonth();
    const q = month < 3 ? 0 : month < 6 ? 3 : month < 9 ? 6 : 9;
    return new Date(d.getFullYear(), q, 1);
  }

  private shiftDays(d: Date, delta: number): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta);
  }

  private addDays(d: Date, delta: number): Date {
    return this.shiftDays(d, delta);
  }

  private format(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parse(s: string): Date {
    const [y, m, d] = s.split('-').map((v) => Number(v));
    return new Date(y, (m || 1) - 1, d || 1);
  }

  private eq(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }
}
